export const SHIFTS = {
  DEFAULT:         { key: 'DEFAULT',         start: '08:00', end: '17:00', label: '08:00 – 17:00' },
  DRIVER_OPERATOR: { key: 'DRIVER_OPERATOR', start: '07:00', end: '16:00', label: '07:00 – 16:00' },
};


export const GRACE_MINUTES = 15;

const val = (v) => (v === null || v === undefined ? '' : String(v).trim());

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Grade names come through as "Worker", "Staff", "Executive", "Driver & Operator", …
export function getShiftForRow(row) {
  const grade = val(row.GradeName).toLowerCase();
  if (grade.includes('driver') || grade.includes('operator')) return SHIFTS.DRIVER_OPERATOR;
  return SHIFTS.DEFAULT;
}

export const hasInTime = (row) => !!val(row.InTime);
export const hasOutTime = (row) => !!val(row.OutTime);
export const isFullPunch = (row) => hasInTime(row) && hasOutTime(row);

// "HH:MM" -> minutes since midnight. Returns null if not parseable.
function timeToMinutes(t) {
  const s = val(t);
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (Number.isNaN(h) || Number.isNaN(mm)) return null;
  return h * 60 + mm;
}

// Latest allowed in-time for this row's shift, in minutes since midnight.
function onTimeCutoff(row) {
  return timeToMinutes(getShiftForRow(row).start) + GRACE_MINUTES;
}

export const isOnTime = (row) => {
  const mins = timeToMinutes(row.InTime);
  return mins !== null && mins <= onTimeCutoff(row);
};

export const isLate = (row) => {
  const mins = timeToMinutes(row.InTime);
  return mins !== null && mins > onTimeCutoff(row);
};

// Minutes late relative to the row's own shift start (0 if on time).
export const minutesLate = (row) => {
  const mins = timeToMinutes(row.InTime);
  if (mins === null) return 0;
  const start = timeToMinutes(getShiftForRow(row).start);
  return Math.max(0, mins - start);
};


function uniqueSorted(rows, key) {
  const set = new Set();
  rows.forEach((r) => {
    const v = val(r[key]);
    if (v) set.add(v);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export function buildFilterOptions(rows) {
  return {
    locations:    uniqueSorted(rows, 'LocationName'),
    grades:       uniqueSorted(rows, 'GradeName'),
    categories:   uniqueSorted(rows, 'EmploymentCategoryName'),
    designations: uniqueSorted(rows, 'DesignationName'),
  };
}

export function applyFilters(rows, filters = {}) {
  const { location, grade, category, designation } = filters;
  return rows.filter((r) => {
    if (location && location !== 'All' && val(r.LocationName) !== location) return false;
    if (grade && grade !== 'All' && val(r.GradeName) !== grade) return false;
    if (category && category !== 'All' && val(r.EmploymentCategoryName) !== category) return false;
    if (designation && designation !== 'All' && val(r.DesignationName) !== designation) return false;
    return true;
  });
}

// ── KPI cards ────────────────────────────────────────────────────────────
export function computeKpis(rows) {
  const totalRecords = rows.length;

  const staffSet = new Set(rows.map((r) => val(r.EmployeeID) || val(r.ServiceNo)));
  const staffAttended = staffSet.size;

  const daySet = new Set(rows.map((r) => normalizeDate(r.AttDate)));
  const activeDays = daySet.size;

  const avgPerDay = activeDays ? totalRecords / activeDays : 0;

  const onTimeCount = rows.filter(isOnTime).length;
  const lateCount = rows.filter(isLate).length;

  const durations = rows
    .filter(isFullPunch)
    .map((r) => {
      const inM = timeToMinutes(r.InTime);
      const outM = timeToMinutes(r.OutTime);
      if (inM === null || outM === null) return null;
      const diff = outM - inM;
      return diff > 0 ? diff : null;
    })
    .filter((d) => d !== null);

  const avgWorkMinutes = durations.length
    ? durations.reduce((a, b) => a + b, 0) / durations.length
    : 0;

  return {
    staffAttended,
    totalRecords,
    activeDays,
    avgPerDay,
    onTimeCount,
    lateCount,
    avgWorkMinutes,
  };
}

export function formatHoursMinutes(totalMinutes) {
  const mins = Math.round(totalMinutes || 0);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

// ── Daily Attendance Trend ──────────────────────────────────────────────
export function normalizeDate(value) {
  const s = val(value);
  if (!s) return '';
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const parsed = new Date(s);
  if (!Number.isNaN(parsed.getTime())) {
    return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(parsed.getDate())}`;
  }
  return s;
}

// Every yyyy-mm-dd between two ISO dates, inclusive.
export function eachDayInRange(from, to) {
  const start = normalizeDate(from);
  const end = normalizeDate(to);
  if (!ISO_DATE_RE.test(start) || !ISO_DATE_RE.test(end)) return [];

  const days = [];
  const cursor = new Date(`${start}T00:00:00`);
  const last = new Date(`${end}T00:00:00`);
  // Guard against an inverted or absurd range.
  let guard = 0;
  while (cursor <= last && guard < 1000) {
    days.push(`${cursor.getFullYear()}-${pad2(cursor.getMonth() + 1)}-${pad2(cursor.getDate())}`);
    cursor.setDate(cursor.getDate() + 1);
    guard += 1;
  }
  return days;
}

export function buildDailyTrend(rows, range) {
  const byDate = new Map();
  rows.forEach((r) => {
    const d = normalizeDate(r.AttDate);
    if (!d) return;
    byDate.set(d, (byDate.get(d) || 0) + 1);
  });

  const rangeDays = range ? eachDayInRange(range.from, range.to) : [];
  const days = rangeDays.length
    ? rangeDays
    : Array.from(byDate.keys()).sort((a, b) => a.localeCompare(b));

  return days.map((date) => ({
    date,                    // full yyyy-mm-dd (used in the tooltip)
    label: date.slice(5),    // mm-dd (axis tick — keeps 31 days readable)
    count: byDate.get(date) || 0,
  }));
}

// ── Punch Summary (Full punch vs Single punch) ──────────────────────────
export function buildPunchSummary(rows) {
  const fullPunch = rows.filter(isFullPunch).length;
  const singlePunch = rows.length - fullPunch;
  return [
    { name: 'Full Punch', value: fullPunch, color: '#10b981' },
    { name: 'Single Punch', value: singlePunch, color: '#f59e0b' },
  ];
}

// ── By Employment Category ───────────────────────────────────────────────
const CATEGORY_COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444', '#14b8a6'];

export function buildByCategory(rows) {
  const map = new Map();
  rows.forEach((r) => {
    const name = val(r.EmploymentCategoryName) || 'Unspecified';
    map.set(name, (map.get(name) || 0) + 1);
  });
  return Array.from(map.entries())
    .sort(([, a], [, b]) => b - a)
    .map(([name, value], i) => ({ name, value, color: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }));
}

// ── In-Time Distribution (by hour) ───────────────────────────────────────
export function buildInTimeDistribution(rows) {
  const buckets = new Map();
  rows.forEach((r) => {
    const mins = timeToMinutes(r.InTime);
    if (mins === null) return;
    const hour = Math.floor(mins / 60);
    const key = `${String(hour).padStart(2, '0')}:00`;
    buckets.set(key, (buckets.get(key) || 0) + 1);
  });
  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([hour, count]) => ({ hour, count }));
}

// ── Attendance by Grade / Designation (horizontal bars) ──────────────────
function buildCountsByField(rows, field, topN) {
  const map = new Map();
  rows.forEach((r) => {
    const name = val(r[field]) || 'Unspecified';
    map.set(name, (map.get(name) || 0) + 1);
  });
  const list = Array.from(map.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
  return topN ? list.slice(0, topN) : list;
}

export const buildByGrade = (rows) => buildCountsByField(rows, 'GradeName');
export const buildByDesignation = (rows) => buildCountsByField(rows, 'DesignationName', 6);

// ── Top Employees by Days Attended ───────────────────────────────────────
export function buildTopEmployees(rows, topN = 6) {
  const map = new Map();
  rows.forEach((r) => {
    const id = val(r.EmployeeID) || val(r.ServiceNo);
    if (!id) return;
    if (!map.has(id)) {
      map.set(id, { id, name: val(r.Name) || `${val(r.FirstName)} ${val(r.LastName)}`.trim(), days: new Set() });
    }
    map.get(id).days.add(normalizeDate(r.AttDate));
  });
  return Array.from(map.values())
    .map((e) => ({ id: e.id, name: e.name, days: e.days.size }))
    .sort((a, b) => b.days - a.days)
    .slice(0, topN);
}

// ── On Time vs Late ───────────────────────────────────────────────────────
export function buildOnTimeVsLate(rows) {
  const onTime = rows.filter(isOnTime).length;
  const late = rows.filter(isLate).length;
  return [
    { name: 'On Time', value: onTime, color: '#10b981' },
    { name: 'Late', value: late, color: '#ef4444' },
  ];
}

// ── Month helpers for the filter bar ─────────────────────────────────────
export const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const pad2 = (n) => String(n).padStart(2, '0');

export function monthRange(year, monthIndex /* 0-11 */) {
  const from = `${year}-${pad2(monthIndex + 1)}-01`;
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  const to = `${year}-${pad2(monthIndex + 1)}-${pad2(lastDay)}`;
  return { from, to };
}

export function toISODate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// Year options for the filter bar: a few years back through next year.
export function buildYearOptions(back = 5, forward = 1) {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = current + forward; y >= current - back; y -= 1) years.push(y);
  return years;
}